import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Actions, ofType, createEffect } from '@ngrx/effects';
import { from, of } from 'rxjs';
import { map, switchMap, catchError, withLatestFrom, mapTo, first } from 'rxjs/operators';
import { AdvanceEventOutcomes, EventsService, Range } from 'ngx-core-services';

import {
  AppState,
  SystemsState,
  getSystemDefinitionConfiguration,
  requestSystemCurrentEvents,
  requestSystemHistoricalEvents,
  selectedSystemMetadata,
  setSystemAlertsError,
  updateSystemCurrentEvents,
  updateSystemHistoricalEvents,
  } from '..';

@Injectable()
export class EventsListEffects {

  requestSystemCurrentEvents$ = createEffect(() =>
    this.actions$.pipe(
      ofType(requestSystemCurrentEvents),
      switchMap((action) =>
        this.store.select(selectedSystemMetadata).pipe(
          first(),
          mapTo(action),
        ),
      ),
      withLatestFrom(this.store),
      switchMap(([action, { systemState }]) =>
        from(this.getSystemCurrentEvent(action.systemId, action.forceRefresh, systemState)).pipe(
          map((events) => updateSystemCurrentEvents({ systemId: action.systemId, instanceId: action.instanceId, events })),
          catchError(_ => of(setSystemAlertsError({ systemId: action.systemId, instanceId: action.instanceId, error: 'Failed to fetch system current event' })))
        )
      )
    )
  );

  requestSystemHistoricalEvents$ = createEffect(() =>
    this.actions$.pipe(
      ofType(requestSystemHistoricalEvents),
      switchMap((action) =>
        this.store.select(selectedSystemMetadata).pipe(
          first(),
          mapTo(action),
        ),
      ),
      withLatestFrom(this.store),
      switchMap(([action, { systemState }]) =>
        from(this.getSystemHistoricalEvent(action.systemId, action.range, action.forceRefresh, systemState)).pipe(
          map((events) => updateSystemHistoricalEvents({ systemId: action.systemId, instanceId: action.instanceId, events})),
          catchError(_ => of(setSystemAlertsError({ systemId: action.systemId, instanceId: action.instanceId, error: 'Failed to fetch system historical event' })))
        )
      )
    )
  );

  constructor(
    private actions$: Actions,
    private store: Store<AppState>,
    private readonly eventsService: EventsService,
  ) {}

  async getSystemCurrentEvent(systemId: string, forceRefresh: boolean, state: SystemsState): Promise<Partial<AdvanceEventOutcomes>[]> {
    const system = state.systemList[systemId];
    if (!system) {
      throw new Error(`System with id ${systemId} not found`);
    }
    return await this.eventsService.fetchCurrentSystemEvent(systemId, forceRefresh);
  }

  async getSystemHistoricalEvent(systemId: string, range: Range, forceRefresh: boolean, state: SystemsState): Promise<Partial<AdvanceEventOutcomes>[]> {
    const system = state.systemList[systemId];
    if (!system) {
      throw new Error(`System with id ${systemId} not found`);
    }

    const systemConfiguration = getSystemDefinitionConfiguration(system);
    return await this.eventsService.fetchDailyHistoricalSystemEvent(systemId, range.from, forceRefresh, `${systemConfiguration.tz}`);
  }
}
